import React from "react";
//#region types
import { TranslationKeys } from "translations";
import { QuotesFormTypes } from "models/formTypes";
import { WintSuperkollAPIModel, WintUtilsWebApi } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { Customer, User } from "models/propTypes/userProfilePropTypes";
import { IconDefinitionConstants } from "utils/constants";
import { TableCellCurrencyFormatterProps } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { Icon, RoundButton, TableAsync, useFilterContext } from "components/common";
import QuoteStatusTags from "./statusTags";
import { SummaryTotalFormatter } from "components/common/tables";
import { useQuotesService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import { useNavigate, useLocation } from "react-router-dom";

const CustomerQuotesList: React.FC = () => {
  const quotesService = useQuotesService();
  const location = useLocation();
  const navigate = useNavigate();

  const { filter } = useFilterContext<QuotesFormTypes.Filter>();

  const filterFunc = React.useMemo(
    () => (baseFilter: WintUtilsWebApi.Filter) => quotesService.getQuotes(baseFilter, filter),
    [filter]
  );

  const onRowClick = (row: WintSuperkollAPIModel.Quotation) => {
    navigate(UrlHelper.formatPath(`/Quotation/${row.serialNumber}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  const onViewInvoiceClick = (row: WintSuperkollAPIModel.Quotation, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(UrlHelper.formatPath(`/Invoice/${row.invoiceId}`), {
      state: { background: location, preserveSearch: true },
    });
  };

  const getMainTotalValue = (row: WintSuperkollAPIModel.Quotation): TableCellCurrencyFormatterProps => {
    const total: TableCellCurrencyFormatterProps = {
      cellValue: row.grandTotalAmount ?? [],
    };

    return total;
  };

  const getOtherTotalValues = (row: WintSuperkollAPIModel.Quotation): TableCellCurrencyFormatterProps[] => {
    const totalWon: TableCellCurrencyFormatterProps = {
      cellValue: row.totalWonOrInvoicedAmount ?? [],
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.WonOrInvoiced),
    };
    const totalLost: TableCellCurrencyFormatterProps = {
      cellValue: row.totalLostAmount ?? [],
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Lost),
    };
    const totalOther: TableCellCurrencyFormatterProps = {
      cellValue: row.totalNoOutcomeAmount ?? [],
      title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.NoOutcome),
    };

    return [totalWon, totalLost, totalOther];
  };

  return (
    <TableAsync<WintSuperkollAPIModel.Quotation>
      properties={{
        serialNumber: {
          description: TranslationKeys.Common.Number,
          helpText: TranslationKeys.Invoice.HelpTextQuoteNumber,
          formatter: (values: WintSuperkollAPIModel.Quotation) => values.serialNumber,
          width: 60,
          noWrap: true,
        },
        ownerId: {
          description: TranslationKeys.Common.Owner,
          helpText: TranslationKeys.Invoice.HelpTextQuoteOwner,
          formatter: (row: WintSuperkollAPIModel.Quotation) => new User(row.ownerId, row.ownerName, true),
          alignment: "center",
          width: 60,
        },
        date: {
          description: TranslationKeys.Common.Sent,
          helpText: TranslationKeys.Invoice.HelpTextQuoteSentDate,
          formatter: (row: WintSuperkollAPIModel.Quotation) => row.date && new Date(row.date),
          noWrap: true,
        },
        endDate: {
          description: TranslationKeys.Common.ExpiryDate,
          helpText: TranslationKeys.Invoice.HelpTextQuoteEndDate,
          formatter: (row: WintSuperkollAPIModel.Quotation) => row.endDate && new Date(row.endDate),
          noWrap: true,
        },
        customerName: {
          description: TranslationKeys.Common.Customer,
          helpText: TranslationKeys.Invoice.HelpTextQuoteCustomer,
          formatter: (row: WintSuperkollAPIModel.RecurringInvoice) => new Customer(row.customerId, row.customerName),
          className: cssExports.textBlue,
          noWrap: true,
        },
        quotationState: {
          description: TranslationKeys.Common.Status,
          helpText: TranslationKeys.Invoice.HelpTextQuoteStatus,
          formatter: (row: WintSuperkollAPIModel.Quotation) => <QuoteStatusTags status={row.quotationState} />,
          noWrap: true,
        },
        totalAmount: {
          description: TranslationKeys.Common.AmountInclVAT,
          helpText: TranslationKeys.Invoice.HelpTextQuoteAmount,
          formatter: (row: WintSuperkollAPIModel.Quotation) => new Amount(row.currency, row.totalAmount),
          summaryValueFormatter: (values: WintSuperkollAPIModel.Quotation) => (
            <SummaryTotalFormatter total={getMainTotalValue(values)} subTotals={getOtherTotalValues(values)} />
          ),
          alignment: "right",
          className: cssExports.textBlack,
          noWrap: true,
        },
        invoiceId: {
          description: TranslationKeys.Common.Invoice,
          helpText: TranslationKeys.Invoice.HelpTextQuoteInvoice,
          formatter: (row: WintSuperkollAPIModel.Quotation) =>
            row.invoiceId ? (
              <RoundButton className={cssExports.tableIconButton} onClick={onViewInvoiceClick.bind(null, row)}>
                <Icon icon={IconDefinitionConstants.WSend} />
              </RoundButton>
            ) : undefined,
          alignment: "center",
          width: 50,
        },
      }}
      orderByProperty={"date"}
      orderByDescending
      includeSummary
      filter={filterFunc}
      pagination="advanced"
      maxRowCount={10}
      rowClickHandler={onRowClick}
    />
  );
};
export default CustomerQuotesList;
